export interface PromisePoolOptions<T, R> {
	concurrency: number;
	onSettle?: (item: T, result: PromiseSettledResult<R>, index: number) => void;
}

export async function runPool<T, R>(
	items: T[],
	worker: (item: T, index: number) => Promise<R>,
	{ concurrency, onSettle }: PromisePoolOptions<T, R>
): Promise<PromiseSettledResult<R>[]> {
	const results = new Array<PromiseSettledResult<R>>(items.length);
	let cursor = 0;

	const runWorker = async (): Promise<void> => {
		while (cursor < items.length) {
			const index = cursor++;
			const item = items[index];
			let result: PromiseSettledResult<R>;
			try {
				result = { status: 'fulfilled', value: await worker(item, index) };
			} catch (reason) {
				result = { status: 'rejected', reason };
			}
			results[index] = result;
			onSettle?.(item, result, index);
		}
	};

	const workerCount = Math.max(1, Math.min(concurrency, items.length));
	await Promise.all(Array.from({ length: workerCount }, runWorker));

	return results;
}

export function emptyStringsToNull<T extends object>(
	obj: T,
	keys?: readonly (keyof T)[]
): T {
	const out = { ...obj } as Record<string, unknown>;
	const target = (keys as readonly string[] | undefined) ?? Object.keys(out);
	for (const key of target) {
		const value = out[key];
		if (typeof value === 'string' && value.trim() === '') {
			out[key] = null;
		}
	}
	return out as unknown as T;
}
